import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { DISCORD_INVITE_URL, DiscordMark, MarketingShell } from '@/components/marketing-shell';
import type { MarketingTheme } from '@/components/marketing-shell';
import { INDUSTRIES } from '@/features/_shared/content/for-industries-data';
import { capture } from '@/lib/posthog';
import { PUBLIC_STUDIO_CTA } from '@/lib/studio-cta';
import { brandCopy, getBrand, setPageSeo } from '@/lib/brand';
import { useLandingMotion } from './lib/use-landing-motion';

const HERO_VIDEO_WEBM_PATH = '/marketing/landing-steps/okrapdf-hero-demo.webm?v=2';
const HERO_VIDEO_MP4_PATH = '/marketing/landing-steps/okrapdf-hero-demo.mp4?v=2';
const HERO_VIDEO_POSTER_PATH = '/marketing/landing-steps/okrapdf-hero-demo-poster.jpg';
// The GitHub org moved okraocr → okracomputer (2026-09-23); the old URLs 404.
// The org page lists the public SDK and CLI repos this section points to.
const GITHUB_REPO_URL = 'https://github.com/okracomputer';
const SELF_HOST_RUNTIME_URL = 'https://github.com/okracomputer/server';

const surfaceChips = ['Claude', 'Codex', 'Cursor', 'MCP', 'CLI', 'API', 'SDK'] as const;

const audienceCards = [
  {
    title: 'Agentic',
    body: 'Prompt Claude, Codex, Cursor, or your own worker. okraPDF gives the agent cited PDF context, typed fields, and actions it can run.',
  },
  {
    title: 'Developers',
    body: 'Build PDF-native products on a TypeScript SDK, REST API, CLI, and MCP-friendly resource catalog. One document graph feeds every surface.',
  },
  {
    title: 'Automation teams',
    body: 'Route invoices, statements, forms, and review packets into webhooks, n8n, Make, Zapier, Sheets, or a human approval queue.',
  },
] as const;

const toolsetItems = [
  {
    label: 'Parse',
    body: 'Turn raw PDFs into page-aware nodes with text, tables, images, and layout metadata.',
  },
  {
    label: 'Context',
    body: 'Give agents token-bounded answers with page, quote, bbox, and proof URLs attached.',
  },
  {
    label: 'Extract',
    body: 'Map PDFs into typed JSON schemas and content-type contracts your app already understands.',
  },
  {
    label: 'Host',
    body: 'Publish permissioned PDF links, previews, and artifacts from the same document ID.',
  },
  {
    label: 'Review',
    body: 'Open durable human gates for extraction, accessibility, redaction, and publish decisions.',
  },
  {
    label: 'Convert',
    body: 'Use everyday PDF utilities when an agent needs HTML, images, Markdown, CSV, or Excel.',
  },
] as const;

const developerBenefits = [
  {
    kicker: 'Open-source',
    title: 'SDK and CLI you can inspect',
    body: 'The agent-facing TypeScript packages are MIT-licensed, versioned, and built around JSON envelopes with next actions.',
    href: GITHUB_REPO_URL,
    cta: 'View GitHub',
  },
  {
    kicker: 'Self-host',
    title: 'Run a local document-graph runtime',
    body: 'The Docker runtime ships Node 22, Poppler, Tesseract, filesystem state, and basic parse, audit, and redact recipes.',
    href: SELF_HOST_RUNTIME_URL,
    cta: 'View self-host runtime',
  },
  {
    kicker: 'TypeScript',
    title: 'Schemas agents can rely on',
    body: 'Zod contracts, typed SDK methods, and content-type manifests keep parse results stable across CLI, API, and MCP.',
    href: 'https://docs.okrapdf.com',
    cta: 'Read docs',
  },
  {
    kicker: 'Community',
    title: 'Bring hard PDFs to Discord',
    body: 'Use the community to debug parser edge cases, self-host setup, agent workflows, and integration recipes.',
    href: DISCORD_INVITE_URL,
    cta: 'Join Discord',
  },
] as const;

const extractionFeatures = [
  {
    label: 'Document graph',
    glyph: 'structure',
    detail: 'Upload once. Agents read pages, tables, images, artifacts, and prior runs from the same document ID.',
  },
  {
    label: 'Page proof',
    glyph: 'grounding',
    detail: 'Every cited answer carries the page, quote, bbox, and proof URL needed to open the exact source region.',
  },
  {
    label: 'Typed fields',
    glyph: 'tables',
    detail: 'Use invoice, statement, contract, or custom schemas; ungrounded fields are routed to review instead of invented.',
  },
  {
    label: 'Workflow handoff',
    glyph: 'confidence',
    detail: 'Send results to webhooks, n8n, Make, Zapier, Sheets, or a human approval gate without rewriting the parse.',
  },
] as const;

type GlyphKind = (typeof extractionFeatures)[number]['glyph'];

const GLYPH_PATHS: Record<GlyphKind, readonly string[]> = {
  structure: ['M8 7h16', 'M8 14h24', 'M8 20h24', 'M8 26h15'],
  grounding: ['M7 5h26v22H7z', 'M13 12h14', 'M13 18h9'],
  confidence: ['M8 26a12 12 0 0 1 24 0', 'M15 19l4 4 7-7'],
  tables: ['M7 5h26v22H7z', 'M7 13h26', 'M19 13v14', 'M28 13v14'],
};

function FeatureGlyph({ kind }: { kind: GlyphKind }) {
  return (
    <svg
      data-glyph
      aria-hidden="true"
      viewBox="0 0 40 32"
      className="h-8 w-10 text-accent-foreground"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {GLYPH_PATHS[kind].map((d) => (
        <path key={d} d={d} data-draw />
      ))}
    </svg>
  );
}

const proofPoints = [
  'One PDF can become cited agent context, typed JSON, hosted links, workflow events, or review tasks.',
  'Every answer keeps page evidence attached, so your agent can cite the source instead of guessing.',
  'Start hosted, then move deterministic workloads to the self-host runtime when privacy demands it.',
] as const;

function updateMeta() {
  setPageSeo({
    title: 'okraPDF: PDF to document intelligence with AI agents',
    description:
      'PDF to document intelligence with AI agents. Use okraPDF to parse, cite, extract, host, review, and self-host PDF workflows with a TypeScript SDK, CLI, API, and MCP surface.',
  });
}

function trackHomeClick(cta: string, destination: string, props?: Record<string, unknown>) {
  capture('marketing_home_cta_clicked', {
    cta,
    destination,
    ...props,
  });
}

function SectionKicker({ children, reveal = false }: { children: ReactNode; reveal?: boolean }) {
  return (
    <p
      {...(reveal ? { 'data-reveal': true } : {})}
      className="flex items-center gap-2.5 text-[13px] font-bold uppercase tracking-widest text-[var(--ember-text)]"
    >
      <span className="kicker-dot" aria-hidden />
      {children}
    </p>
  );
}

export function MarketingHome({ theme }: { theme?: MarketingTheme }) {
  const motionRoot = useRef<HTMLElement | null>(null);

  useEffect(() => {
    updateMeta();
  }, []);

  useLandingMotion(motionRoot);

  return (
    <MarketingShell forceTheme={theme}>
      <main ref={motionRoot}>
        <LandingHero />
        <AudienceSection />
        <ExtractionApiSection />
        <AgentToolsetSection />
        <DeveloperBenefitsSection />
        <IndustriesSection />
        <ProofStrip />
      </main>
    </MarketingShell>
  );
}

function LandingHero() {
  return (
    <section className="relative overflow-hidden bg-[var(--paper)]">
      <div className="hero-aurora" aria-hidden />
      <div
        className="okra-dev-grid hero-grid-mask absolute inset-0 opacity-20 sm:opacity-30"
        aria-hidden
      />
      <div className="relative mx-auto grid max-w-7xl items-center gap-6 px-4 pb-8 pt-8 sm:px-6 sm:pb-10 sm:pt-10 lg:grid-cols-[minmax(0,0.95fr)_minmax(460px,1.05fr)] lg:px-8 lg:pb-10 lg:pt-10">
        <div className="flex flex-col justify-center animate-slide-up">
          <span className="glass-badge okra-dev-mono inline-flex w-fit items-center gap-2 rounded-full px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-[var(--ember-text)]">
            <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-[var(--ember)]" aria-hidden />
            Agentic PDF intelligence
          </span>
          <h1 className="hero-h1-fit mt-6 bg-gradient-to-br from-[var(--ink)] via-[var(--ink)] to-[var(--ink-soft)] bg-clip-text font-semibold leading-[1.04] tracking-tight text-transparent">
            PDF to document intelligence with{' '}
            <span className="text-gradient-ember relative inline-block whitespace-nowrap">
              AI agents
              <svg
                data-bbox-frame
                aria-hidden="true"
                className="pointer-events-none absolute -inset-x-2 -inset-y-1 h-[calc(100%+0.5rem)] w-[calc(100%+1rem)] text-accent-foreground opacity-80"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                fill="none"
              >
                <path
                  data-draw
                  d="M2 3h96v94H2z"
                  stroke="currentColor"
                  strokeWidth={2}
                  vectorEffect="non-scaling-stroke"
                />
              </svg>
            </span>
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-[var(--ink)] sm:text-xl">
            {brandCopy(
              'Plan, parse, cite, and route PDF work automatically from Claude, Codex, Cursor, MCP, CLI, and API workflows. okraPDF gives agents a TypeScript document graph with page evidence, typed extraction, and production handoffs.',
              getBrand(),
            )}
          </p>

          <div className="mt-7 flex flex-col gap-4 sm:flex-row sm:items-center">
            <a
              href={PUBLIC_STUDIO_CTA.href}
              onClick={() => trackHomeClick('hero_studio', PUBLIC_STUDIO_CTA.href)}
              className="cta-primary inline-flex h-12 items-center justify-center gap-2 rounded-full px-7 text-[15px] font-medium text-[var(--paper-raised)]"
            >
              {PUBLIC_STUDIO_CTA.label}
              <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href={DISCORD_INVITE_URL}
              onClick={() => trackHomeClick('hero_discord', DISCORD_INVITE_URL)}
              className="cta-ghost inline-flex h-12 items-center justify-center gap-2 rounded-full px-7 text-[15px] font-medium text-[var(--ink)]"
            >
              <DiscordMark className="h-4 w-4 shrink-0" />
              Discord Support
            </a>
          </div>

          <div className="mt-6 flex flex-wrap gap-x-4 gap-y-2 text-sm text-[var(--ink-mute)]">
            <Link
              to="/pdf-agents"
              onClick={() => trackHomeClick('hero_pdf_agents', '/pdf-agents')}
              className="font-medium text-[var(--ember-text)] underline-offset-4 hover:underline"
            >
              I need agentic PDF intelligence
            </Link>
            <Link
              to="/tools"
              onClick={() => trackHomeClick('hero_pdf_tools', '/tools')}
              className="font-medium text-[var(--ember-text)] underline-offset-4 hover:underline"
            >
              I need normal PDF tools
            </Link>
            <a
              href={SELF_HOST_RUNTIME_URL}
              onClick={() => trackHomeClick('hero_self_host', SELF_HOST_RUNTIME_URL)}
              className="font-medium text-[var(--ember-text)] underline-offset-4 hover:underline"
            >
              I need self-host
            </a>
          </div>

          <div className="mt-9 flex flex-wrap items-center gap-2">
            <span className="okra-dev-mono text-[10px] font-semibold uppercase tracking-widest text-[var(--ink-mute)]">
              Runs inside
            </span>
            {surfaceChips.map((chip) => (
              <span
                key={chip}
                className="okra-dev-mono rounded-full bg-[var(--paper-raised)] px-2.5 py-1 text-[11px] font-medium text-[var(--ink-soft)] ring-1 ring-[var(--line-soft)]"
              >
                {chip}
              </span>
            ))}
          </div>
        </div>

        <HeroWorkflowVideo />
      </div>
    </section>
  );
}

function AudienceSection() {
  return (
    <section className="relative bg-[var(--paper-sunk)] py-20 sm:py-24">
      <div className="gradient-divider" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl" data-reveal-group>
          <SectionKicker reveal>{brandCopy('Who is okraPDF for?', getBrand())}</SectionKicker>
          <h2 data-reveal className="mt-4 text-[34px] font-semibold tracking-tight text-[var(--ink)] sm:text-[60px]">
            Teams turning PDFs into <span className="whitespace-nowrap">agent-ready</span> work.
          </h2>
        </div>
        <div className="mt-10 grid gap-4 md:grid-cols-3" data-reveal-group>
          {audienceCards.map((card) => (
            <div
              key={card.title}
              data-reveal
              className="stripe-card rounded-xl p-6"
            >
              <p className="okra-dev-mono text-[12px] font-semibold uppercase tracking-wide text-[var(--ember-text)]">
                {card.title}
              </p>
              <p className="mt-4 text-[15px] leading-relaxed text-[var(--ink-mute)]">
                {brandCopy(card.body, getBrand())}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HeroWorkflowVideo() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);

  useEffect(() => {
    const node = videoRef.current;
    if (!node || shouldLoadVideo || typeof window === 'undefined') return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return;

    if (!('IntersectionObserver' in window)) {
      setShouldLoadVideo(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setShouldLoadVideo(true);
        observer.disconnect();
      },
      { rootMargin: '600px 0px' },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [shouldLoadVideo]);

  useEffect(() => {
    if (!shouldLoadVideo || !videoRef.current) return;
    void videoRef.current.play().catch(() => undefined);
  }, [shouldLoadVideo]);

  return (
    <div className="self-center overflow-hidden rounded-2xl bg-[var(--paper-raised)] shadow-[0_40px_80px_-32px_rgba(0,0,0,0.28)] ring-1 ring-black/5 animate-slide-up delay-200 dark:ring-white/10">
      <div className="flex items-center gap-3 border-b border-[var(--line-soft)] bg-[var(--paper-sunk)] px-4 py-2.5">
        <div className="flex gap-1.5" aria-hidden>
          <span className="h-2.5 w-2.5 rounded-full bg-[var(--line)]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[var(--line)]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[var(--line)]" />
        </div>
        <div className="okra-dev-mono mx-auto flex items-center rounded-md bg-[var(--paper-raised)] px-3 py-1 text-[11px] text-[var(--ink-mute)] ring-1 ring-[var(--line-soft)]">
          {new URL(getBrand().origins.app).hostname}
        </div>
        <div className="w-[42px]" aria-hidden />
      </div>
      <video
        ref={videoRef}
        className="block aspect-[126/91] h-auto w-full bg-[var(--paper)]"
        autoPlay={shouldLoadVideo}
        muted
        loop
        playsInline
        preload={shouldLoadVideo ? 'metadata' : 'none'}
        poster={HERO_VIDEO_POSTER_PATH}
        width={1260}
        height={910}
        aria-label={brandCopy(
          'Animated okraPDF document workflow: a sample invoice moves from page preview to template extraction to clean JSON, CSV, or webhook export.',
          getBrand(),
        )}
      >
        {shouldLoadVideo ? (
          <>
            <source src={HERO_VIDEO_WEBM_PATH} type="video/webm" />
            <source src={HERO_VIDEO_MP4_PATH} type="video/mp4" />
          </>
        ) : null}
      </video>
    </div>
  );
}

function ExtractionApiSection() {
  return (
    <section className="relative bg-[var(--paper-sunk)] py-24 sm:py-32">
      <div className="gradient-divider" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl" data-reveal-group>
          <SectionKicker reveal>Agent workflows</SectionKicker>
          <h2 data-reveal className="mt-4 text-[36px] font-semibold tracking-tight text-[var(--ink)] sm:text-[60px]">
            Run the PDF loop end to end: parse, ask, extract, review, publish.
          </h2>
          <p data-reveal className="mt-5 text-[16px] leading-relaxed text-[var(--ink-mute)]">
            {brandCopy(
              'okraPDF is the document intelligence layer under your agent. It gives each PDF a durable graph, proof links, typed contracts, and workflow actions across the same API, CLI, and MCP surface.',
              getBrand(),
            )}
          </p>
        </div>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-reveal-group data-glyph-grid>
          {extractionFeatures.map((feature) => (
            <div
              key={feature.label}
              data-reveal
              className="stripe-card rounded-xl p-5"
            >
              <div className="flex items-start justify-between">
                <span className="icon-chip h-11 w-12">
                  <FeatureGlyph kind={feature.glyph} />
                </span>
              </div>
              <h3 className="mt-3 text-[16px] font-semibold tracking-tight text-[var(--ink)]">
                {feature.label}
              </h3>
              <p className="mt-2 text-[14px] leading-relaxed text-[var(--ink-mute)]">
                {feature.detail}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AgentToolsetSection() {
  return (
    <section className="relative bg-[var(--paper)] py-24 sm:py-32">
      <div className="gradient-divider" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl" data-reveal-group>
          <SectionKicker reveal>AI and automation</SectionKicker>
          <h2 data-reveal className="mt-4 text-[36px] font-semibold tracking-tight text-[var(--ink)] sm:text-[60px]">
            All the tools required for agentic PDF intelligence in one place.
          </h2>
          <p data-reveal className="mt-5 max-w-3xl text-[16px] leading-relaxed text-[var(--ink-mute)]">
            The same document graph powers the hosted app, API keys, CLI, MCP tools, and workflow
            automations, so agents do not have to stitch together separate PDF utilities.
          </p>
        </div>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-reveal-group>
          {toolsetItems.map((item, index) => (
            <div
              key={item.label}
              data-reveal
              className="stripe-card rounded-xl p-6"
            >
              <div className="flex items-start justify-between gap-4">
                <h3 className="text-[17px] font-semibold tracking-tight text-[var(--ink)]">
                  {item.label}
                </h3>
                <span className="icon-chip okra-dev-mono h-7 w-7 text-[11px] font-semibold text-[var(--ember-text)]">
                  {String(index + 1).padStart(2, '0')}
                </span>
              </div>
              <p className="mt-3 text-[14px] leading-relaxed text-[var(--ink-mute)]">
                {item.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DeveloperBenefitsSection() {
  return (
    <section className="relative bg-[var(--paper-raised)] py-24 sm:py-32">
      <div className="gradient-divider" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end" data-reveal-group>
          <div className="max-w-3xl md:max-w-4xl" data-reveal>
            <SectionKicker>Developer benefits</SectionKicker>
            <h2 className="h2-sentence-break mt-4 text-[36px] font-semibold tracking-tight text-[var(--ink)] sm:text-[60px]">
              <span className="whitespace-nowrap">Open-source</span> where it should be.{' '}
              <span className="whitespace-nowrap">Self-host</span> where it matters.
            </h2>
            <p className="mt-5 max-w-2xl text-[16px] leading-relaxed text-[var(--ink-mute)]">
              {brandCopy(
                'okraPDF is TypeScript-native first: SDK, CLI, schemas, recipes, and a local runtime path for teams that need control over document data.',
                getBrand(),
              )}
            </p>
          </div>
          <a
            href={SELF_HOST_RUNTIME_URL}
            onClick={() => trackHomeClick('developer_benefits_self_host', SELF_HOST_RUNTIME_URL)}
            data-reveal
            className="inline-flex h-11 w-fit shrink-0 items-center justify-center gap-2 rounded-full bg-[var(--paper-sunk)] px-6 text-[14px] font-medium text-[var(--ink)] transition-colors hover:bg-[var(--line-soft)]"
          >
            Explore self-host
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-reveal-group>
          {developerBenefits.map((benefit) => (
            <a
              key={benefit.kicker}
              href={benefit.href}
              onClick={() => trackHomeClick('developer_benefit', benefit.href, { benefit: benefit.kicker })}
              data-reveal
              className="stripe-card group rounded-xl p-6"
            >
              <p className="okra-dev-mono text-[11px] font-semibold uppercase tracking-wide text-[var(--ember-text)]">
                {benefit.kicker}
              </p>
              <h3 className="mt-3 text-[17px] font-semibold tracking-tight text-[var(--ink)] transition-colors group-hover:text-[var(--ember-text)]">
                {benefit.title}
              </h3>
              <p className="mt-3 text-[14px] leading-relaxed text-[var(--ink-mute)]">
                {benefit.body}
              </p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--ember-text)]">
                {benefit.cta}
                <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function IndustriesSection() {
  return (
    <section className="relative bg-[var(--paper)] py-24 sm:py-32">
      <div className="gradient-divider" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end" data-reveal-group>
          <div className="max-w-3xl" data-reveal>
            <SectionKicker>Industries</SectionKicker>
            <h2 className="mt-3 text-[36px] font-semibold tracking-tight text-[var(--ink)] sm:text-[60px]">
              Built for the documents your industry runs on.
            </h2>
            <p className="mt-5 max-w-2xl text-[16px] leading-relaxed text-[var(--ink-mute)]">
              Start from the PDFs your team already processes, then route the extracted rows into
              the systems each workflow depends on.
            </p>
          </div>
          <Link
            to="/for"
            onClick={() => trackHomeClick('see_all_industries', '/for')}
            data-reveal
            className="inline-flex h-11 w-fit shrink-0 items-center justify-center gap-2 rounded-full bg-[var(--paper-sunk)] px-6 text-[14px] font-medium text-[var(--ink)] transition-colors hover:bg-[var(--line-soft)]"
          >
            See all industries
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-reveal-group>
          {INDUSTRIES.map((industry) => (
            <Link
              key={industry.slug}
              to={`/for/${industry.slug}`}
              onClick={() =>
                trackHomeClick('industry_card', `/for/${industry.slug}`, {
                  slug: industry.slug,
                })
              }
              data-reveal
              className="stripe-card group rounded-2xl p-6"
            >
              <h3 className="text-[17px] font-semibold tracking-tight text-[var(--ink)] transition-colors group-hover:text-[var(--ember-text)]">
                {industry.name}
              </h3>
              <p className="mt-3 text-[14px] leading-relaxed text-[var(--ink-mute)]">
                {industry.tagline}
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProofStrip() {
  return (
    <section className="relative bg-[var(--paper-raised)] py-20 sm:py-24">
      <div className="gradient-divider" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3" data-reveal-group>
          {proofPoints.map((point) => (
            <div
              key={point}
              data-reveal
              className="flex min-h-[88px] items-center gap-5 rounded-2xl bg-[var(--paper)] p-6 text-[15px] leading-relaxed text-[var(--ink-soft)] shadow-sm ring-1 ring-black/5 dark:ring-white/10"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--ember-tint)] text-[var(--ember-text)] shadow-sm">
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </span>
              <span>{point}</span>
            </div>
          ))}
        </div>
        <div
          className="mt-12 flex flex-col items-center gap-4 sm:flex-row sm:justify-center sm:gap-6"
          data-reveal-group
          data-reveal
        >
          <a
            href="/blog/"
            onClick={() =>
              trackHomeClick('proof_strip_integrations', '/blog/', {
                surface: 'proof_strip',
              })
            }
            className="inline-flex items-center gap-1.5 text-[15px] font-medium text-[var(--ink-mute)] transition-colors hover:text-[var(--ink)]"
          >
            Browse integration guides
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
}
